import { useCallback, useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { JobRow } from './JobRow'
import type { JobName, JobSchedule, JobStatus } from '@/api/types'

interface JobsSectionProps<TJob extends JobName> {
  title: string
  description: string
  labels: Record<TJob, string>
  descriptions: Record<TJob, string>
  loadStatuses: () => Promise<JobStatus<TJob>[]>
  runJob: (job: TJob) => Promise<void>
  updateSchedule: (job: TJob, cron: string) => Promise<JobSchedule>
}

const POLL_INTERVAL_MS = 3000
const LOADING_MESSAGE = 'Laddar jobb...'
const LOAD_ERROR_MESSAGE = 'Kunde inte hämta jobbens status just nu.'

export function JobsSection<TJob extends JobName>({
  title,
  description,
  labels,
  descriptions,
  loadStatuses,
  runJob,
  updateSchedule
}: JobsSectionProps<TJob>): React.ReactElement {
  const [statuses, setStatuses] = useState<JobStatus<TJob>[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isAnyJobRunning = statuses.some((status) => status.running)

  const refreshStatuses = useCallback(async (): Promise<void> => {
    try {
      setStatuses(await loadStatuses())
      setErrorMessage(null)
    } catch {
      setErrorMessage(LOAD_ERROR_MESSAGE)
    } finally {
      setIsLoading(false)
    }
  }, [loadStatuses])

  useEffect(() => {
    void refreshStatuses()
  }, [refreshStatuses])

  const handleSaveSchedule = async (job: TJob, cron: string): Promise<void> => {
    await updateSchedule(job, cron)
    await refreshStatuses()
  }

  useEffect(() => {
    if (!isAnyJobRunning) {
      return
    }

    const interval = window.setInterval(() => void refreshStatuses(), POLL_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [isAnyJobRunning, refreshStatuses])

  return (
    <Card className="gap-5 overflow-hidden pb-0 shadow-none border-border/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <th className="py-3 pl-6 pr-3 font-medium">Jobb</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium">Schema</th>
                <th className="px-3 py-3 font-medium">Senast körd</th>
                <th className="px-3 py-3 font-medium">Resultat</th>
                <th className="py-3 pl-3 pr-6">
                  <span className="sr-only">Åtgärd</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((status) => (
                <JobRow
                  key={status.job}
                  label={labels[status.job]}
                  description={descriptions[status.job]}
                  status={status}
                  onRun={() => runJob(status.job)}
                  onStarted={refreshStatuses}
                  onSaveSchedule={(cron) => handleSaveSchedule(status.job, cron)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {isLoading && <p className="px-6 py-6 text-sm text-muted-foreground">{LOADING_MESSAGE}</p>}
        {errorMessage && <p className="border-t border-border/70 px-6 py-4 text-sm text-destructive">{errorMessage}</p>}
      </CardContent>
    </Card>
  )
}
